/**
 * Tries to retrieve session storage data from the given key
 *
 * See <https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage> for more info.
 * N.b. All session storage data is cast to strings.
 *
 * Args:
 *      storageKey (String): Item's key.
 *
 * Returns:
 *      (String) Tries to get the session storage for the given key; otherwise, returns `false`.
 */
export function getCacheData(storageKey) {
    const hasStorage = 'sessionStorage' in window && window.sessionStorage;
    let now;
    let expiration;
    let data;

    try {
        if (hasStorage) {
            data = sessionStorage.getItem(storageKey);
            if (data) {
                // extract saved object from JSON encoded string
                data = JSON.parse(data);

                // calculate expiration time for content,
                // to force periodic refresh after 10 minutes
                now = new Date();
                expiration = new Date(data.timestamp);
                expiration.setMinutes(expiration.getMinutes() + 10);

                // ditch the content if too old
                if (now.getTime() > expiration.getTime()) {
                    data = false;
                    sessionStorage.removeItem(storageKey);
                } else if (data.content) {
                    data = data.content;
                } else {
                    data = false;
                }
            }
        }
    } catch (e) {
        data = false;
    }

    return data;
}

/**
 * Sets the key-value pair for session storage
 *
 * See <https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage> for more info.
 *
 * Args:
 *      data (*): Item's value.
 *      storageKey (String): Item's key.
 *
 * Returns:
 *      (Undefined) Tries to set the browser's session storage, given the key-value pair.
 */
export function setCacheData(data, storageKey) {
    try {
        sessionStorage.setItem(
            storageKey,
            JSON.stringify({
                timestamp: new Date(),
                content: data,
            }),
        );
    } catch (e) {
        // silently suppress, it doesn't really matter
    }
}

/**
 * Clears session storage data given for the given key
 *
 * See <https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage> for more info.
 *
 * Args:
 *      storageKey (String): Item's key.
 *
 * Returns:
 *      (Undefined) Removes item from browser's session storage.
 */
export function clearCacheData(storageKey) {
    sessionStorage.removeItem(storageKey);
}
